import { canNavigate, ConductorAllChildrenActive, Router } from "@frui.ts/screens";
import { action, computed, observable } from "mobx";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import ActionsViewModel from "./actions/actionsViewModel";
import SearchViewModel from "./search/searchViewModel";
import EventBus from "services/eventBus";
import { callCenterViewModelName, vehicleSearchStatus } from "services/eventBus";
import VehicleRegisterVehicleSearchStatus from "entities/vehicleRegisterVehicleSearchStatus";
import CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";

@Router.registerRoute({
  route: Modules.callCenter,
  name: Router.Self,
  children: [SearchViewModel],
})
export default class CallCenterViewModel extends ConductorAllChildrenActive<ActionsViewModel | SearchViewModel> {
  private unsubscribtions: Array<() => void> = [];
  @observable vehicleSearchStatus: VehicleRegisterVehicleSearchStatus | null;
  @observable callCenterViewModelName: CallCenterModelNamesEnum;

  constructor(
    public actionsViewModel: ActionsViewModel,
    public searchViewModel: SearchViewModel,
    private securityService: SecurityService,
    private localizationService: LocalizationService,
    private eventBus: EventBus
  ) {
    super();

    this.navigationName = Modules.callCenter;
    if (this.securityService.allowTo("callcenter") || this.securityService.allowTo("enforcement.callcenter")) {
      this.children.push(this.searchViewModel);
    }

    if (this.allowWrite) {
      this.children.push(this.actionsViewModel);
    }
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscribtions.push(
      this.eventBus.subscribe(callCenterViewModelName, this.onCallCenterViewModelChanged),
      this.eventBus.subscribe(vehicleSearchStatus, this.onVehicleSearchStatusChanged)
    );
    return super.onInitialize();
  }

  async navigate(subPath: string | undefined, params: any) {
    const childToActivate = subPath ? this.searchViewModel : undefined;

    if (childToActivate) {
      await this.tryActivateChild(childToActivate);

      if (canNavigate(childToActivate)) {
        await childToActivate.navigate(subPath, params);
      }
    }
    await this.onChildNavigated(childToActivate);
  }

  @computed
  get allowWrite() {
    return this.securityService.allowTo("callcenter", "write");
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get displayVehicleRegisterWarning() {
    return (
      this.callCenterViewModelName === CallCenterModelNamesEnum.VehicleRegisterVehicle &&
      this.vehicleSearchStatus !== VehicleRegisterVehicleSearchStatus.SearchSuccessful
    );
  }

  @action.bound
  onVehicleSearchStatusChanged(event: ReturnType<typeof vehicleSearchStatus>) {
    this.vehicleSearchStatus = event.payload.vehicleSearchStatus;
  }

  @action.bound
  onCallCenterViewModelChanged(event: ReturnType<typeof callCenterViewModelName>) {
    this.callCenterViewModelName = event.payload.callCenterViewModelName;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, code, size);
  }

  translateVehicleSearchStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleRegister.${code}`, size);
  }
}
